import { resources } from "@constants/hardcoded-strings";
import { ErrorAlert } from "@containers/common/error-alert/error-alert";
import { TabTitleWithErrorCount } from "@containers/common/tab-title-with-error-count/tab-title-with-error-count";
import { GeneralAdvancedValidationErrors } from "@idw-types/common/validation/common/general-advanced-validation-errors";
import { FormattedRpServiceConfigurationAttribute, FormattedRpServiceConfigurationProperty } from "@idw-types/data/rp-service-configuration";
import { getLoginHintInfo } from "@utils/login-hint/login-hint-utils";
import { isNullOrUndefined } from "@utils/object-util";
import { getPinCodeProperties } from "@utils/pin-code/pin-code-utils";
import { FormattedAppliedProfile } from "@idw-types/data/applied-profile/applied-profile";
import { getWebhookConfigurationProperties } from "@utils/webhook/webhook-utils";
import { getSessionTokenInfo } from "@utils/custom-session-timeout/custom-session-timeout-util";
import {
    isStringValidAlphaNumericWithUnderscoreDash,
    isStringValidAlphaNumeric,
    isStringValidUrlRegex,
    isStringValidNumberRegex
} from "@utils/string-util";
import { POPULATE_CREDENTIAL_FROM_ATTRIBUTE_PROPERTY_ID } from "@constants/prefilled-credential";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { getIncodeSmsInfo } from "@utils/incode-sms/incode-sms-util";
import { SecureWebhookTypes } from "@enums/secure-webhook-types";
import { LoginHintExpirationTypeTypes } from "@enums/login-hint-expiration-type-types";

export const getGeneralAdvancedValidationErrors = (
    properties: Array<FormattedRpServiceConfigurationProperty>,
    appliedProfiles: Array<FormattedAppliedProfile>,
    attributes: Array<FormattedRpServiceConfigurationAttribute>,
    showPinCodeSection: boolean
): GeneralAdvancedValidationErrors => {
    let count = 0;
    let loginHintPublicKeyEmptyError = null;
    let loginHintPrivateKeyEmptyError = null;
    let loginHintExpirationTypeError = null;
    let loginHintExpirationValueError = null;
    let pinResendAndValidationAttemptsError = null;
    let pinTimeoutError = null;
    let staticPinCodeError = null;
    let rpWebhookUrlError = null;
    let rpWebhookFileNameError = null;
    let rpWebhookWrapperObjectError = null;
    let rpWebhookAuthUserError = null;
    let rpWebhookAuthPassError = null;
    let rpWebhookClientIDError = null;
    let rpWebhookClientSecretError = null;
    let rpWebhookTokenUrlError = null
    let asiTimeoutError = null;
    let prefilledCredentialAttributeError = null;
    let incodeSmsMessagingServiceError = null;

    const {
        isLoginHintJWTEnabled,
        isOidcTokenUserAttributeEnabled,
        isWTRSAPublicKeyEnabled,
        isWTRSAPrivateKeyEnabled,
        tRSAPrivateKeyPropertyValue,
        tRSAPublicKeyPropertyValue,
        isExpireLoginHintEnabled,
        loginHintExpirationValue
    } = getLoginHintInfo(properties, appliedProfiles);

    const advancedResources = resources.pages.verificationServiceDetails.tabs.general.subTabs.advanced;

    //LOGIN HINT
    if (isLoginHintJWTEnabled && isOidcTokenUserAttributeEnabled && isWTRSAPublicKeyEnabled) {
        if (!tRSAPublicKeyPropertyValue) {
            loginHintPublicKeyEmptyError = <ErrorAlert>
                <span>{advancedResources.sections.loginHint.errors.noJWTPublicKeyErrorText}</span>
            </ErrorAlert>;
            count++;
        }
    }

    if (isLoginHintJWTEnabled && isOidcTokenUserAttributeEnabled && isWTRSAPrivateKeyEnabled) {
        if (!tRSAPrivateKeyPropertyValue) {
            loginHintPrivateKeyEmptyError = <ErrorAlert>
                <span>{advancedResources.sections.loginHint.errors.noJWTPrivateKeyErrorText}</span>
            </ErrorAlert>;
            count++;
        }
    }

    if (isExpireLoginHintEnabled) {
        if (isNullOrUndefined(loginHintExpirationValue.type)) {
            loginHintExpirationTypeError = <ErrorAlert>
                <span>{resources.common.requiredField}</span>
            </ErrorAlert>;
            count++;
        }

        if (isNullOrUndefined(loginHintExpirationValue.value) || loginHintExpirationValue.value === "") {
            loginHintExpirationValueError = <ErrorAlert>
                <span>{resources.common.requiredField}</span>
            </ErrorAlert>;
            count++;
        } else {
            if (loginHintExpirationValue.type === LoginHintExpirationTypeTypes.DURATION) {
                const isValid = isStringValidNumberRegex(loginHintExpirationValue.value);
                if (!isValid) {
                    loginHintExpirationValueError = <ErrorAlert>
                        <span>{"Please enter a valid duration in minutes"}</span>
                    </ErrorAlert>;
                    count++;
                }
            }
        }
    }

    //PIN CODE
    if (showPinCodeSection) {
        const { maxPinSendCount, pinTimeout, hardcodedPinCode } = getPinCodeProperties(properties, appliedProfiles);

        if (!isNullOrUndefined(maxPinSendCount)) {
            const maxPinSendCountNumber = parseInt(maxPinSendCount);

            if (
                maxPinSendCount === "" ||
                !/^\d+$/.test(maxPinSendCount) ||
                maxPinSendCountNumber > 50 ||
                maxPinSendCountNumber < 2
            ) {
                pinResendAndValidationAttemptsError = <ErrorAlert>
                    <span>{advancedResources.sections.pinCode.inputs.pinResendAndValidationAttempts.validationErrors.required}</span>
                </ErrorAlert>;
                count++;
            }
        }

        if (!isNullOrUndefined(pinTimeout)) {
            const pinTimeoutNumber = parseInt(pinTimeout);

            if (
                pinTimeout === "" ||
                !/^\d+$/.test(pinTimeout) ||
                pinTimeoutNumber < 60
            ) {
                pinTimeoutError = <ErrorAlert>
                    <span>{advancedResources.sections.pinCode.inputs.pinTimeout.validationErrors.required}</span>
                </ErrorAlert>;
                count++;
            }
        }

        if (!isNullOrUndefined(hardcodedPinCode)) {
            if (
                hardcodedPinCode === "" ||
                !/^\d+$/.test(hardcodedPinCode) ||
                hardcodedPinCode.length !== 6
            ) {
                staticPinCodeError = <ErrorAlert>
                    <span>{advancedResources.sections.pinCode.inputs.staticPinCode.validationErrors.required}</span>
                </ErrorAlert>;
                count++;
            }
        }
    }

    //WEBHOOK
    const {
        postToRPWebhookPropertyValue,
        rpWebhookUrlPropertyValue,
        rpWebhookFileNamePropertyValue,
        rpWebhookWrapperObjectPropertyValue,
        rpWebhookAuthTypePropertyValue,
        rpWebhookAuthPassPropertyValue,
        rpWebhookAuthUserPropertyValue,
        rpWebhookTokenUrlPropertyValue,
        rpWebhookTokenClientIDPropertyValue,
        rpWebhookTokenClientSecretPropertyValue
    } = getWebhookConfigurationProperties(properties, appliedProfiles);

    if (isStringTrue(postToRPWebhookPropertyValue)) {
        if (!isNullOrUndefined(rpWebhookFileNamePropertyValue) && rpWebhookFileNamePropertyValue !== "") {
            if (!isStringValidAlphaNumericWithUnderscoreDash(rpWebhookFileNamePropertyValue)) {
                rpWebhookFileNameError = <ErrorAlert>
                    <span>{advancedResources.sections.webhookConfiguration.inputs.webhookFileName.validationErrors.invalid}</span>
                </ErrorAlert>
                count++;
            }
        }

        //
        if (typeof rpWebhookUrlPropertyValue !== "undefined" && !isStringValidUrlRegex(rpWebhookUrlPropertyValue)) {
            rpWebhookUrlError = <ErrorAlert>
                <span>{advancedResources.sections.webhookConfiguration.inputs.webhookUrl.validationErrors.required}</span>
            </ErrorAlert>;
            count++;
        }

        if (typeof rpWebhookWrapperObjectPropertyValue !== "undefined") {
            if (
                rpWebhookWrapperObjectPropertyValue !== null && (
                    rpWebhookWrapperObjectPropertyValue === "" ||
                    !isStringValidAlphaNumeric(rpWebhookWrapperObjectPropertyValue)
                )
            ) {
                rpWebhookWrapperObjectError = <ErrorAlert>
                    <span>{advancedResources.sections.webhookConfiguration.inputs.webhookWrapperObjectName.validationErrors.required}</span>
                </ErrorAlert>;
                count++;
            }
        }
        if (!isNullOrUndefined(rpWebhookAuthTypePropertyValue)) {
            if (rpWebhookAuthTypePropertyValue === SecureWebhookTypes.BASIC) {
                if (
                    !isNullOrUndefined(rpWebhookAuthUserPropertyValue) &&
                    (rpWebhookAuthUserPropertyValue === "" || rpWebhookAuthUserPropertyValue.includes(" "))
                ) {
                    rpWebhookAuthUserError = <ErrorAlert>
                        <span>{advancedResources.sections.webhookConfiguration.inputs.webhookAuthUser.validationErrors.required}</span>
                    </ErrorAlert>;
                    count++;
                }

                if (
                    !isNullOrUndefined(rpWebhookAuthPassPropertyValue) &&
                    (rpWebhookAuthPassPropertyValue === "" || rpWebhookAuthPassPropertyValue.includes(" ") || rpWebhookAuthPassPropertyValue.length < 8)
                ) {
                    rpWebhookAuthPassError = <ErrorAlert>
                        <span>{advancedResources.sections.webhookConfiguration.inputs.webhookAuthPass.validationErrors.required}</span>
                    </ErrorAlert>;
                    count++;
                }
            }
            if (rpWebhookAuthTypePropertyValue === SecureWebhookTypes.OAUTH) {
                if (
                    !isNullOrUndefined(rpWebhookTokenClientIDPropertyValue) &&
                    (rpWebhookTokenClientIDPropertyValue === "" || rpWebhookTokenClientIDPropertyValue.includes(" "))
                ) {
                    rpWebhookClientIDError = <ErrorAlert>
                        <span>{advancedResources.sections.webhookConfiguration.inputs.webhookTokenClientID.validationErrors.required}</span>
                    </ErrorAlert>;
                    count++;
                }
                if (
                    !isNullOrUndefined(rpWebhookTokenClientSecretPropertyValue) &&
                    (rpWebhookTokenClientSecretPropertyValue === "" || rpWebhookTokenClientSecretPropertyValue.includes(" "))
                ) {
                    rpWebhookClientSecretError = <ErrorAlert>
                        <span>{advancedResources.sections.webhookConfiguration.inputs.webhookTokenClientSecret.validationErrors.required}</span>
                    </ErrorAlert>;
                    count++;
                }
                if (
                    !isNullOrUndefined(rpWebhookTokenUrlPropertyValue) &&
                    (rpWebhookTokenUrlPropertyValue === "" || rpWebhookTokenUrlPropertyValue.includes(" "))
                ) {
                    rpWebhookTokenUrlError = <ErrorAlert>
                        <span>{advancedResources.sections.webhookConfiguration.inputs.webhookTokenUrl.validationErrors.required}</span>
                    </ErrorAlert>;
                    count++;
                }
            }
        }

    }

    // CUSTOM SESSION TIMEOUT
    const { asiTimeoutPropertyValue, customTokenTimeoutPropertyValue } = getSessionTokenInfo(properties, appliedProfiles);
    if (!isNullOrUndefined(asiTimeoutPropertyValue) && !isNullOrUndefined(customTokenTimeoutPropertyValue)) {
        if (
            asiTimeoutPropertyValue === "" ||
            !/^\d+$/.test(asiTimeoutPropertyValue)
        ) {
            asiTimeoutError = <ErrorAlert>
                <span>{advancedResources.sections.sessionCustomization.validationErrors.asiTimeout.asiTimeoutValueError}</span>
            </ErrorAlert>;
            count++;
        }
    }

    // PREFILLED CREDENTIAL 
    const isPrefilledCredentialEnabled = properties.some(property => property.propertyTypeId === POPULATE_CREDENTIAL_FROM_ATTRIBUTE_PROPERTY_ID && property.addedToService);
    if (isPrefilledCredentialEnabled) {
        const hasAtLeastOnePopulateAttribute = attributes.some(attribute => isStringTrue(attribute.populateCredentialFromThisAttribute));
        if (!hasAtLeastOnePopulateAttribute) {
            prefilledCredentialAttributeError = <ErrorAlert>
                <span>{advancedResources.sections.prefilledCredentials.validationErrors.selectAttribute.prefilledCredentialAttributeError}</span>
            </ErrorAlert>;
            count++;
        }
    }

    // Incode SMS
    const { isIncodeSmsEnabled, docCaptureSoNamePropertyValue } = getIncodeSmsInfo(properties, appliedProfiles);
    if (isIncodeSmsEnabled && !docCaptureSoNamePropertyValue) {
        incodeSmsMessagingServiceError = (
            <ErrorAlert>
                <span>{resources.common.requiredField}</span>
            </ErrorAlert>
        );
        count++;
    }

    return {
        loginHintPublicKeyEmptyError,
        loginHintPrivateKeyEmptyError,
        loginHintExpirationTypeError,
        loginHintExpirationValueError,
        pinResendAndValidationAttemptsError,
        pinTimeoutError,
        staticPinCodeError,
        rpWebhookUrlError,
        rpWebhookFileNameError,
        rpWebhookWrapperObjectError,
        rpWebhookAuthUserError,
        rpWebhookAuthPassError,
        rpWebhookClientIDError,
        rpWebhookClientSecretError,
        rpWebhookTokenUrlError,
        asiTimeoutError,
        prefilledCredentialAttributeError,
        incodeSmsMessagingServiceError: incodeSmsMessagingServiceError,
        count,
        tabTitle: <TabTitleWithErrorCount tabName={advancedResources.tabName} errorCount={count} />
    };
};
